/** Login page with password and optional TOTP 2FA. */

import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../state/auth'

export function LoginPage() {
  const { isAuthenticated, isLoading, error, pendingTotpToken, login, verifyTotp } = useAuthStore()
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')

  // FE-011: Redirect authenticated users away from login page
  if (isAuthenticated) return <Navigate to="/" replace />

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    await login(password)
  }

  const handleTotp = async (e: FormEvent) => {
    e.preventDefault()
    await verifyTotp(totpCode)
  }

  return (
    <div className="aegis-shell flex min-h-screen items-center justify-center">
      <div className="aegis-panel w-full max-w-sm rounded-2xl p-8 shadow-xl">
        <h1 className="mb-2 text-center text-2xl font-bold text-[var(--aegis-bone)]">Aegis</h1>
        <p className="mb-6 text-center text-sm text-[var(--aegis-fog)]">Autonomous Fund Cockpit</p>

        {error && (
          <div className="mb-4 rounded bg-red-900/50 px-3 py-2 text-sm text-red-300">{error}</div>
        )}

        {pendingTotpToken ? (
          <form onSubmit={handleTotp}>
            <label className="mb-1 block text-sm text-slate-400">TOTP Code</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className="mb-4 w-full rounded bg-bg-tertiary px-3 py-2 text-center text-lg tracking-widest text-slate-100 outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="000000"
              autoFocus
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || totpCode.length !== 6}
              className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <label className="mb-1 block text-sm text-slate-400">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-4 w-full rounded bg-bg-tertiary px-3 py-2 text-slate-100 outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password"
              autoFocus
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !password}
              className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
